import { keepPreviousData } from "@tanstack/react-query";
import mapValues from "lodash/mapValues";
import merge from "lodash/merge";
import { useActivityPermissions } from "PFApp/activities/hooks";
import { useMatches } from "PFCore/hooks/queries/matches/use_matches";
import { MatchesDistribution } from "PFCore/types/matches_distribution";
import { MatchFetchState, MetaWithPagination } from "PFTypes";
import { useMemo } from "react";

import { useMatchesStore } from "../store/matches";
import { useCurrentActivity } from "./use_current_activity";

export type MatchesMeta = MetaWithPagination<{
  distribution?: MatchesDistribution;
}>;

const DEFAULT_MATCHES_META: MatchesMeta = {
  totalPages: 0,
  order: {
    options: [],
    value: { direction: "", text: "", name: [] },
    name: "",
    title: "",
    type: ""
  },
  filters: {},
  total: 0,
  page: 1,
  perPage: 10
};

const getMatchesFetchState = (isFetching: boolean, isSuccess: boolean, error: { status: number } | null) => {
  if (isFetching) {
    return MatchFetchState.Pending;
  }

  if (isSuccess) {
    return MatchFetchState.Synced;
  }

  const { status } = error || {};
  if (status && [502, 504].includes(status)) {
    return MatchFetchState.Timeout;
  } else {
    return MatchFetchState.Error;
  }
};

export const useActivityMatches = () => {
  const activity = useCurrentActivity();
  const { canSeeMatches } = useActivityPermissions(activity);
  const matchesParams = useMatchesStore((state) => state.matchesParams);

  const isClosed = activity.state === "complete";
  const {
    data: matchesData,
    isFetching,
    isSuccess,
    error
  } = useMatches(activity.id, matchesParams, {
    placeholderData: keepPreviousData,
    enabled: canSeeMatches && !!activity.id && !isClosed,
    gcTime: 0
  });

  const matchesMeta = useMemo(() => {
    if (!matchesData?.meta) {
      return DEFAULT_MATCHES_META;
    }

    const mainFilters = matchesData.meta.filtersV2?.mainFilters;
    // TODO: [SP-7094] configuring 'required' here should not be needed once filters have 'mandatory' property
    if (mainFilters) {
      merge(
        mainFilters,
        mapValues(mainFilters, () => ({
          required: false
        }))
      );
    }
    return matchesData.meta as MatchesMeta;
  }, [matchesData?.meta]);

  const matchesFetchState = getMatchesFetchState(isFetching, isSuccess, error);

  return {
    matches: matchesData?.entries,
    matchesMeta,
    matchesFetchState,
    canSeeMatches
  };
};
